import { z } from 'zod';
import apiClient from '@shared/api/http';
import { toItemArray, toSnakeCaseKeys } from '@shared/api/keys';
import type {
  Datacenter,
  DatacenterId,
  DatacenterWritePayload,
  SalaOfDatacenter,
} from '../domain/datacenter';

// v1 (`DatacenterCatalogItemDto`/`DatacenterDetailDto`) identifica el país por ISO-2
// (`countryCode`) y ya no devuelve su nombre: el dominio sigue hablando de `countryId`, que aquí
// transporta ese ISO-2 — `countryOption.repository.ts` publica las opciones con la misma clave.
const DatacenterDtoSchema = z.object({
  id: z.string(),
  code: z.string().optional(),
  name: z.string(),
  city: z.string().nullish(),
  address: z.string().nullish(),
  country_code: z.string().nullish(),
  country_name: z.string().nullish(),
  status: z.string().optional(),
  version: z.number().optional(),
});

const SalaOfDatacenterDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  floor: z.string().nullish(),
  datacenter_id: z.string().optional(),
});

// v1 sustituyó el estado `inactive` por `retired`; el dominio y la UI siguen usando `inactive`.
const statusFromV1 = (status?: string) => (status === 'retired' ? 'inactive' : status);
const statusToV1 = (status: string) => (status === 'inactive' ? 'retired' : status);

function toDomain(dto: z.infer<typeof DatacenterDtoSchema>): Datacenter {
  return {
    id: dto.id as DatacenterId,
    name: dto.name,
    city: dto.city ?? undefined,
    address: dto.address ?? undefined,
    countryId: dto.country_code ?? undefined,
    countryName: dto.country_name ?? dto.country_code ?? undefined,
    status: statusFromV1(dto.status),
  };
}

function toSalaOfDatacenterDomain(dto: z.infer<typeof SalaOfDatacenterDtoSchema>): SalaOfDatacenter {
  return {
    id: dto.id,
    name: dto.name,
    floor: dto.floor ?? undefined,
  };
}

// `code` es obligatorio al crear en v1 y no existe en el formulario as-is: se deriva del nombre
// (mayúsculas alfanuméricas) — ver improvements.md, candidato a campo propio en la UI.
function toDto(payload: DatacenterWritePayload) {
  return {
    code: payload.name.toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 20),
    name: payload.name,
    city: payload.city,
    address: payload.address,
    countryCode: payload.countryId,
    status: statusToV1(payload.status),
  };
}

export const datacenterRepository = {
  async getAll(params?: { status: string }): Promise<Datacenter[]> {
    const { data } = params
      ? await apiClient.get('/datacenters', { params: { status: statusToV1(params.status) } })
      : await apiClient.get('/datacenters');
    return z
      .array(DatacenterDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .map(toDomain);
  },

  async getById(id: DatacenterId): Promise<Datacenter | null> {
    const { data } = await apiClient.get(`/datacenters/${id}`);
    if (!data) return null;
    return toDomain(DatacenterDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  // v1 no cuelga las salas del datacenter: se listan desde `/rooms` y se filtran por datacenter.
  async getSalas(id: DatacenterId): Promise<SalaOfDatacenter[]> {
    const { data } = await apiClient.get('/rooms');
    return z
      .array(SalaOfDatacenterDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .filter((s) => s.datacenter_id === id)
      .map(toSalaOfDatacenterDomain);
  },

  async create(payload: DatacenterWritePayload): Promise<Datacenter> {
    const { data } = await apiClient.post('/datacenters', toDto(payload));
    return toDomain(DatacenterDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  // v1 exige la `version` leída para actualizar (concurrencia optimista).
  async update(id: DatacenterId, payload: DatacenterWritePayload): Promise<Datacenter> {
    const { data: current } = await apiClient.get(`/datacenters/${id}`);
    const { version } = DatacenterDtoSchema.parse(toSnakeCaseKeys(current));
    const { data } = await apiClient.patch(`/datacenters/${id}`, { ...toDto(payload), version });
    return toDomain(DatacenterDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  // Deja pasar el error del backend tal cual (spec.md UC-6: sin chequeo previo de
  // salas asociadas en frontend) — el mapeo a mensaje legible ocurre en application/.
  async remove(id: DatacenterId): Promise<void> {
    await apiClient.delete(`/datacenters/${id}`);
  },
};
